import { type FC, type FormEvent, useCallback, useState } from 'react';

import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { LinkIcon } from '@heroicons/react/24/outline';
import { type Editor, useEditorState } from '@tiptap/react';
import cn from 'classnames';

import { ICON_CLASS, POPOVER_PANEL_CLASSES, tr } from '../constants';
import { isSingleUrl } from '../nodes/LinkCard/LinkCard';
import { ToolbarButton } from '../toolbar/ToolbarButton';

interface LinkPopoverProps {
  editor: Editor;
  translations?: Record<string, string>;
}

export const LinkPopover: FC<LinkPopoverProps> = ({ editor, translations }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [invalid, setInvalid] = useState(false);

  const floating = useFloating({
    open,
    onOpenChange: (next) => {
      setOpen(next);
      setInvalid(false);
      if (next) {
        const href = editor.getAttributes('link').href;
        setUrl(typeof href === 'string' ? href : '');
      }
    },
    placement: 'top-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const interactions = useInteractions([useClick(floating.context), useDismiss(floating.context)]);

  const editorState = useEditorState({
    editor,
    selector: ({ editor: e }) => ({ isLink: e.isActive('link') }),
  });

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = url.trim();
      // setLink/unsetLink come from @tiptap/extension-link installed by the parent app.
      // biome-ignore lint/suspicious/noExplicitAny: see comment above
      const chain = editor.chain().focus() as any;
      if (!trimmed) {
        chain.extendMarkRange('link').unsetLink().run();
        setOpen(false);
        return;
      }
      const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      if (!isSingleUrl(href)) {
        setInvalid(true);
        return;
      }
      if (editor.state.selection.empty && !editor.isActive('link')) {
        chain.insertLinkCard(href, { manual: true }).run();
      } else {
        chain.extendMarkRange('link').setLink({ href }).insertLinkCard(href).run();
      }
      setOpen(false);
    },
    [editor, url],
  );

  const handleRemove = useCallback(() => {
    // biome-ignore lint/suspicious/noExplicitAny: see comment in handleSubmit
    (editor.chain().focus() as any).extendMarkRange('link').unsetLink().run();
    setOpen(false);
  }, [editor]);

  return (
    <>
      <ToolbarButton
        label={tr(translations, 'community.composer.add_link')}
        icon={<LinkIcon className={ICON_CLASS} aria-hidden="true" />}
        active={editorState.isLink || open}
        buttonRef={floating.refs.setReference}
        buttonProps={interactions.getReferenceProps()}
      />
      {open && (
        <FloatingPortal>
          <form
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            onSubmit={handleSubmit}
            className={cn(POPOVER_PANEL_CLASSES, 'flex items-start gap-2 p-2 z-[100]')}
            {...interactions.getFloatingProps()}
          >
            <div className="flex flex-col gap-1">
              <input
                type="text"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setInvalid(false);
                }}
                placeholder={tr(translations, 'community.composer.link_url_placeholder')}
                // biome-ignore lint/a11y/noAutofocus: focus is expected when popover opens
                autoFocus
                className="w-64 px-3 py-1.5 text-sm rounded-full bg-black/5 outline-none focus:ring-2 focus:ring-black/20"
              />
              {invalid && (
                <p role="alert" className="px-3 text-[13px] leading-4 text-red-500">
                  {tr(translations, 'community.composer.link_invalid')}
                </p>
              )}
            </div>
            <button
              type="submit"
              className="inline-flex h-8 items-center justify-center rounded-full px-4 text-[13px] font-medium bg-black text-white hover:bg-black/90"
            >
              {tr(translations, 'community.composer.link_save')}
            </button>
            {editorState.isLink && (
              <button type="button" onClick={handleRemove} className="text-sm text-black/60 hover:text-black px-2">
                {tr(translations, 'community.composer.link_remove')}
              </button>
            )}
          </form>
        </FloatingPortal>
      )}
    </>
  );
};
